import { OctLabel, OctNumberField } from '@oceantech/oceantech-ui';
import { getIn, useFormikContext } from 'formik';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import useIndexDBValue from '../../../../../../hook/useIndexDBValue';
import AutocompleteV2 from '../../../../../component/AutocompleteObjectV2';
import DatePickerCustom from '../../../../../component/date-picker/DatePickerCustom';
import LabelRequired from '../../../../../component/LabelRequired';
import TextField from '../../../../../component/TextField';
import { IDanhSachDanhMuc } from '../../../../../phan-he-tiep-nhan-thanh-toan/models/TiepDonModel';
import { APP_TIMESTAMP_FORMAT, KEY_DS_DANH_MUC_TIEP_DON } from '../../../../../utils/Constant';
import { formatDateTime } from '../../../../../utils/FormatUtils';
import ICD10 from '../../../../components/ICD10';
import ProvisonalDiagnosis from '../../../../components/ProvisonalDiagnosis';
import { IKhoThuoc } from '../../../../models/ThuocModels';
import { getWarehouseByVisitCode } from '../../../modal-ke-vat-tu/common';
import NgayKeTuDong from '../NgayKeTuDong';

type Props = {
	generalInfo?: any;
	disabledWarehouse?: boolean;
	isEdit?: boolean;
};

const OrderInfo = ({ generalInfo, disabledWarehouse, isEdit }: Props) => {
	const { values, touched, errors, setFieldValue } = useFormikContext<any>();
	const { value: categories } = useIndexDBValue<IDanhSachDanhMuc>({
		key: KEY_DS_DANH_MUC_TIEP_DON,
	});

	const [warehouses, setWarehouses] = useState<IKhoThuoc[]>([]);
	const [shouldOpenModalSelectICD10, setShouldOpenModalSelectICD10] = useState(false);
	const [shouldOpenModalICD10, setShouldOpenModalICD10] = useState(false);
	const [listComorbidity, setListComorbidity] = useState<any[]>([]);

	const { isExamined, visit } = generalInfo ?? {};
	const formValues = values?.orderInfo;

	useEffect(() => {
		getWarehouses();
	}, []);

	const getWarehouses = async () => {
		try {
			const data: any = await getWarehouseByVisitCode(
				visit?.visitObjectTypeCode,
				'drug',
				generalInfo,
			);
			setWarehouses(data);
		} catch (error) {
			console.error(error);
		}
	};

	const getWarehouseValue = (id: string) => {
		if (formValues?.warehouse) return formValues.warehouse;

		if (!warehouses?.length || !id) return;

		return warehouses.find((record) => record.id === parseInt(id));
	};

	const onFieldChange = (field: string, data: any) => {
		setFieldValue(`orderInfo.${field}`, data);
	};

	return (
		<div className="bg-white radius-2 spaces py-10 px-10">
			<div className="d-flex flex-wrap">
				<div className="spaces mb-4">
					<DatePickerCustom
						label="Ngày y lệnh"
						name="doctorOrderDay"
						labelClassName="spaces ms-0 min-w-120"
						value={formValues?.doctorOrderDay}
						setDateValue={(date) => {
							onFieldChange('doctorOrderDay', date);
						}}
						mode="date-time"
					/>
				</div>
				<div className="spaces position-relative d-flex align-items-start">
					<Button
						className="bg-white spaces py-0 pr-5 mb-3"
						onClick={() => {
							setShouldOpenModalSelectICD10(true);
						}}
					>
						<u className="fw-bold text-pri fs-13px">Chọn bệnh</u>
					</Button>
					<LabelRequired label="Chẩn đoán" isRequired className="min-w-90px ms-2" />
				</div>
				<Row className="flex-grow-1 position-relative">
					<ProvisonalDiagnosis
						encounterDiagnosisValues={formValues}
						encounterDiagnosisErrors={getIn(errors, 'orderInfo')}
						encounterDiagnosisTouched={getIn(touched, 'orderInfo')}
						disabled={!isExamined}
						listComorbidity={listComorbidity}
						setListComorbidity={setListComorbidity}
						onChange={(data: any) => {
							setFieldValue('orderInfo', {
								...values.orderInfo,
								...data,
							});
						}}
						handleChangeEncounterDiagnosis={(name, value) => {
							onFieldChange(name, value);
						}}
					/>
				</Row>
			</div>
			<Row className="spaces mt-4">
				<Col xs={6} className="d-flex">
					<div className="d-flex">
						<LabelRequired
							label="Bệnh kèm theo"
							className="d-inline-block spaces min-w-120-px text-truncate"
						/>
					</div>
					<div className="spaces position-relative w-100">
						<TextField
							className="w-100 d-flex flex-column align-items-start"
							name="comorbidity"
							labelClassName="ms-0 min-w-145px"
							value={formValues?.comorbidity}
							onChange={(e: ChangeEvent<HTMLInputElement>) => {
								onFieldChange('comorbidity', e.target.value);
							}}
						/>
					</div>
				</Col>
				<Col xs={6}>
					<TextField
						className="spaces w-100"
						inputClassName="w-100"
						label="Ghi chú"
						name="note"
						labelClassName="ms-0 min-w-85px"
						value={formValues?.note}
						onChange={(e: ChangeEvent<HTMLInputElement>) => {
							onFieldChange('note', e.target.value);
						}}
					/>
				</Col>
			</Row>
			<Row className="spaces mt-4">
				<Col xs={3} className="d-flex">
					<LabelRequired label="Kho thuốc" className="min-w-120-px" />
					<AutocompleteV2
						options={warehouses}
						name="warehouse"
						value={getWarehouseValue(values?.warehouseId || '')}
						onChange={(selectedOption) => {
							onFieldChange('warehouse', selectedOption);
						}}
						isDisabled={disabledWarehouse}
					/>
				</Col>
				<Col xs={3}>
					<TextField
						className="spaces w-100"
						inputClassName="w-100"
						label="BS điều trị"
						name="orderInfo.treatmentDoctorName"
						labelClassName="spaces ms-0 min-w-120"
						disabled
					/>
				</Col>
				<Col xs={6}>
					<TextField
						className="spaces w-100"
						inputClassName="w-100"
						label="Lời dặn BS"
						name="doctorAdvice"
						labelClassName="ms-0 spaces min-w-85"
						value={formValues?.doctorAdvice}
						onChange={(e: ChangeEvent<HTMLInputElement>) => {
							onFieldChange('doctorAdvice', e.target.value);
						}}
					/>
				</Col>
			</Row>
			<Row className="spaces mt-4">
				<Col xs={3} className="d-flex">
					<OctLabel label="Đợt dùng" className="spaces min-w-120" />
					<OctNumberField
						name="usageTime"
						value={formValues?.usageTime}
						onChange={(value: number) => {
							onFieldChange('usageTime', value);
						}}
						containerClassName="flex-1"
					/>
				</Col>
				<Col xs={3}>
					<DatePickerCustom
						label="Ngày tái khám"
						name="reExaminationDate"
						labelClassName="ms-0 spaces min-w-120"
						value={formValues?.reExaminationDate}
						setDateValue={(date) => {
							onFieldChange(
								'reExaminationDate',
								formatDateTime(date, APP_TIMESTAMP_FORMAT)
							);
						}}
						minDate={new Date()}
					/>
				</Col>
				{!isEdit && (
					<Col xs={6}>
						<NgayKeTuDong name="ngayKeTuDong" />
					</Col>
				)}
			</Row>

			<ICD10
				shouldOpenModalICD10={shouldOpenModalICD10}
				setShouldOpenModalICD10={setShouldOpenModalICD10}
				shouldOpenModalSelectICD10={shouldOpenModalSelectICD10}
				setShouldOpenModalSelectICD10={setShouldOpenModalSelectICD10}
				listICD10={categories?.listICD10 ?? []}
				listComorbidity={listComorbidity}
				setListComorbidity={setListComorbidity}
				selectionModeICD10="radio"
				data={formValues}
				onChange={(value) => {
					setFieldValue('orderInfo', {
						...values.orderInfo,
						...value,
					});
				}}
			/>
		</div>
	);
};

export default OrderInfo;
